<script setup lang="ts">
import {useFreeResources} from '~/composables/cms/free-resources'
import {cx} from 'class-variance-authority'
import CategoryCard from '~/components/free-resources/category-card.vue'
import ResourceCard from '~/components/free-resources/resource-card.vue'
import FreeResourcesPixelGrid from '~/components/free-resources/free-resources-pixel-grid.vue'

const {allResources, fetchPageData, filterByCategory} = useFreeResources()
const route = useRoute()

const {
  public: {baseUrl},
} = useRuntimeConfig()

const pixelGridLg = [
  '121212121212121212121212121212121212121212121212121',
  '212121212121212121212121212121212121212121212121212',
  '121212121212121212121212121212121212121212121212121',
  '212121212121212121212121212121212121212121212121212',
  '121212121212121212121212121212121212121212121212121',
  '212121212121212121212121212121212121212121212121212',
  '121212121212121212121212121212121212121212121212121',
  '212121212121212121212121212121212121212121212121212',
  '121212121212121212121212121212121212121212121212121',
].join('\n')

const {data: initialData} = await useAsyncData('resources-page', () => fetchPageData())
const {data: freeResources} = useLivePreview({initialData: initialData.value, depth: 2})

const selectedCategory = computed(() => {
  const category = route.query.category as string
  return category?.toLowerCase() || 'all'
})

const seo = freeResources.value?.meta

useSeoMeta({
  title: seo?.title,
  description: seo?.description,
  ogUrl: `${baseUrl}/free-resources`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})

const gradient = 'bg-gradient-to-b bg-clip-text text-transparent leading-tight'

watch(
  selectedCategory,
  async newCategory => {
    await filterByCategory(newCategory)
  },
  {immediate: true}
)

const scrollToResources = () => {
  const resourcesSection = document.getElementById('resources')
  if (resourcesSection) {
    resourcesSection.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }
}
</script>

<template>
  <Container v-if="freeResources?.hero" class="relative flex flex-col items-center space-y-6 pt-28">
    <Heading size="caption" as="h1">
      <span>{{ freeResources.hero.eyebrow }}</span>
    </Heading>

    <Heading
      as="h2"
      class="line-height-[0.1rem] w-11/12 text-center text-4xl xs:mb-8 lg:w-2/3 lg:text-6xl lg:tracking-[-0.16rem] xl:w-1/3"
    >
      <span class="text-gray-900">{{ $t('free_resources.title') }}</span>
      <br />
      <span :class="cx(gradient, 'from-gray-700 to-gray-500')">{{ $t('free_resources.subtitle') }}</span>
    </Heading>

    <Paragraph class="w-10/12 text-center text-sm text-gray-600 lg:w-full lg:text-lg" size="medium">
      <span>{{ freeResources.hero.smallDescription }}</span>
    </Paragraph>

    <Action @click="scrollToResources" size="lg" intent="main">
      {{ $t('free_resources.cta', 'See all resources') }}
    </Action>

    <NuxtImg
      class="w-full min-w-[50rem] flex-shrink-0 pt-1 lg:w-2/4"
      v-bind="resolveMediaProps(freeResources.hero.illustration)"
    />

    <template #outer>
      <FreeResourcesPixelGrid
        class="!pointer-events-auto absolute top-0 z-0 h-screen w-full lg:flex"
        size="large"
        :gradient="true"
        :pattern="pixelGridLg"
      />
    </template>
  </Container>

  <Container v-if="freeResources" class="flex w-full justify-center border-b border-t">
    <div class="flex w-full max-w-3xl gap-2 py-8">
      <CategoryCard v-for="(category, index) in freeResources.resourcesCategories" :key="index" :category="category" />
    </div>
  </Container>

  <Container id="resources" class="flex w-full scroll-p-4 justify-center overflow-auto py-10">
    <div class="w-full max-w-4xl">
      <div class="space-y-6">
        <NuxtLink
          v-for="resource in allResources"
          :key="resource.id"
          :to="`/free-resources/${resource.slug}`"
          class="block w-full max-w-3xl transition-opacity duration-300 hover:opacity-80"
        >
          <div class="flex justify-center">
            <ResourceCard direction="row" :resource="resource" />
          </div>
        </NuxtLink>
      </div>
    </div>
  </Container>
</template>

<style scoped>
.spin {
  animation: spin 500ms linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.resource-list-enter-active {
  transition: all 0.3s ease-out;
}

.resource-list-leave-active {
  transition: all 0.15s ease-in;
}

.resource-list-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.resource-list-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.resource-list-move {
  transition: transform 0.3s ease;
}
</style>
